"use client";
import React, { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { useSelector, useDispatch } from "react-redux";
import { Alert, Button, Snackbar, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ConfirmDialog from "@/app/components/modals/ConfirmDialog";
import {
  dismissUnverifiedEmailDialog,
  resetUnverifiedEmailDialog,
} from "@/store/apps/notifier/notifierDataSlice";

export default function UnverifiedEmailNotifier({
  hasOtherNotifications = false,
  onActiveChange = () => {},
}) {
  const router = useRouter();
  const theme = useTheme();
  const dispatch = useDispatch();
  const profile = useSelector((state) => state.user.profile);
  const dialogDismissed = useSelector(
    (state) => state.notifications.unverifiedEmailDialogDismissed,
  );

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [checkingComplete, setCheckingComplete] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(
    profile?.email_verification_status || "pending",
  );

  const hasCheckedOnce = useRef(false);

  const isLg = useMediaQuery(theme.breakpoints.up("lg"));
  const isEmailUnverified = profile?.email_verified === false;

  useEffect(() => {
    if (verificationStatus !== "pending") {
      setCheckingComplete(true);
      return;
    }

    if (hasCheckedOnce.current) {
      setCheckingComplete(true);
      return;
    }

    hasCheckedOnce.current = true;

    const timer = setTimeout(async () => {
      try {
        const response = await fetch("/api/user/email-verification-status");
        if (response.ok) {
          const data = await response.json();
          setVerificationStatus(data.status);
        }
      } catch (error) {
        console.error("Error checking verification status:", error);
        setVerificationStatus("failed");
      } finally {
        setCheckingComplete(true);
      }
    }, 20000);

    return () => clearTimeout(timer);
  }, [verificationStatus]);

  useEffect(() => {
    setVerificationStatus(profile?.email_verification_status);
  }, [profile?.email_verification_status]);

  useEffect(() => {
    if (!isEmailUnverified) {
      dispatch(resetUnverifiedEmailDialog());
    }
  }, [isEmailUnverified, dispatch]);

  useEffect(() => {
    if (!profile) {
      onActiveChange(false);
      return;
    }

    if (
      !checkingComplete ||
      verificationStatus === "pending" ||
      verificationStatus === "unknown"
    ) {
      setShowSnackbar(false);
      setShowDialog(false);
      onActiveChange(false);
      return;
    }

    if (
      verificationStatus !== "failed" ||
      !isEmailUnverified ||
      hasOtherNotifications
    ) {
      setShowSnackbar(false);
      setShowDialog(false);
      onActiveChange(false);
      return;
    }

    if (isLg) {
      setShowSnackbar(true);
      setShowDialog(false);
      onActiveChange(false);
    } else {
      setShowSnackbar(false);
      if (!dialogDismissed) {
        setShowDialog(true);
        onActiveChange(true);
      } else {
        setShowDialog(false);
        onActiveChange(false);
      }
    }
  }, [
    profile,
    checkingComplete,
    verificationStatus,
    isEmailUnverified,
    isLg,
    hasOtherNotifications,
    dialogDismissed,
    onActiveChange,
  ]);

  const handleSnackbarClose = () => {
    setShowSnackbar(false);
  };

  const handleDialogClose = () => {
    setShowDialog(false);
    dispatch(dismissUnverifiedEmailDialog());
    onActiveChange(false);
  };

  const handleUpdateEmail = () => {
    setShowSnackbar(false);
    setShowDialog(false);
    dispatch(dismissUnverifiedEmailDialog());
    onActiveChange(false);
    router.push("/settings");
  };

  if (
    !checkingComplete ||
    verificationStatus === "pending" ||
    verificationStatus === "unknown" ||
    !isEmailUnverified
  ) {
    return null;
  }

  return (
    <>
      {isLg && (
        <Snackbar
          open={showSnackbar}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          sx={{
            top: "16px !important",
            width: { xs: "100%", md: "670px" },
          }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity="info"
            sx={{
              width: "100%",
              backgroundColor: "transparent",
              color: theme.palette.text.primary,
              boxShadow: "none",
              border: "none",
              display: "flex",
              alignItems: "center",
              "& .MuiAlert-message": {
                fontSize: "1rem",
                display: "flex",
                alignItems: "center",
                padding: "8px 0",
              },
              "& .MuiAlert-icon": {
                color: theme.palette.warning.main,
                marginRight: "12px",
                display: "flex",
                alignItems: "center",
                alignSelf: "center",
                padding: 0,
              },
              "& .MuiAlert-action": {
                padding: "0 8px",
                marginRight: 0,
                alignSelf: "center",
              },
            }}
            action={
              <Button
                color="warning"
                size="medium"
                variant="contained"
                onClick={handleUpdateEmail}
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                  },
                  fontWeight: 600,
                  py: 0.35,
                  my: "auto",
                  alignSelf: "center",
                  fontSize: "0.90rem",
                }}
              >
                Update
              </Button>
            }
          >
            Email not verified. Use a valid email for meetings updates.
          </Alert>
        </Snackbar>
      )}

      {!isLg && (
        <ConfirmDialog
          open={showDialog && !hasOtherNotifications}
          onClose={handleDialogClose}
          onConfirm={handleUpdateEmail}
          title="Email not verified"
          confirmButtonText="Update email"
          ignoreButtonText="I'll Do It Later"
          showIgnoreButton={false}
          showCloseIcon={true}
          body={
            <>
              We couldn't verify <strong>{profile?.email}</strong>.<br />
              <br />
              Please use a valid email so you can receive all meeting updates.
            </>
          }
        />
      )}
    </>
  );
}
